import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { graphQlClient } from '@config/graphqlClient';
import TasksGraphQL from '@graphql/task.queries';
import { Task, getTaskRequest } from '@models/task.model';
import { useTranslation } from '@hooks/useTranslation';

/** Query key prefix of the project task-list query; the org-events subscription invalidates by it. */
export const TASKS_QUERY_KEY = 'tasks';

interface CancelDelegationResponse {
  cancelDelegation: Task;
}

interface UseTasksReturn {
  tasks: Task[];
  loading: boolean;
  error: string | null;
  fetchTasks: (projectId: string) => void;
  fetchTaskById: (taskId: string) => Promise<Task | null>;
  setTasks: (tasks: Task[]) => void;
  refetch: () => void;
  cancelDelegation: (taskId: string) => Promise<Task | null>;
  updateTask: (taskUpdateInput: any) => Promise<Task | null>;
}

// API functions
const fetchTasksAPI = async (projectId: string): Promise<Task[]> => {
  if (!projectId) {
    return [];
  }

  const data: getTaskRequest = await graphQlClient.request(
    TasksGraphQL.queries.getTasks,
    { projectId },
  );

  return data.getTasks || [];
};

const cancelDelegationAPI = async (taskId: string): Promise<Task> => {
  const data: CancelDelegationResponse = await graphQlClient.request(
    TasksGraphQL.mutations.cancelDelegation,
    { taskId },
  );

  return data.cancelDelegation;
};

const updateTaskAPI = async (taskUpdateInput: any): Promise<Task> => {
  const data: { updateTask: Task } = await graphQlClient.request(
    TasksGraphQL.mutations.updateTask,
    { task: taskUpdateInput },
  );

  return data.updateTask;
};

const fetchTaskByIdAPI = async (taskId: string): Promise<Task> => {
  const data: { getTaskById: Task } = await graphQlClient.request(
    TasksGraphQL.queries.getTaskById,
    { taskId },
  );

  return data.getTaskById;
};

export const useTasks = (projectId?: string): UseTasksReturn => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  // Main tasks query
  const {
    data: tasks = [],
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: [TASKS_QUERY_KEY, projectId],
    queryFn: () => fetchTasksAPI(projectId!),
    enabled: !!projectId,
  });

  // Handle query error
  React.useEffect(() => {
    if (queryError) {
      const errorMessage =
        (queryError as any)?.response?.errors[0]?.message ||
        'Error loading tasks';
      toast.error(errorMessage);
    }
  }, [queryError]);

  // Cancel delegation mutation
  const cancelDelegationMutation = useMutation({
    mutationFn: cancelDelegationAPI,
    onSuccess: (data, taskId) => {
      toast.success(tActionMessages.delegationCancelledSuccessfully);

      // Update tasks query cache
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.errors[0]?.message || 'Error cancelling delegation';
      toast.error(errorMessage);
    },
  });

  // Update task mutation
  const updateTaskMutation = useMutation({
    mutationFn: updateTaskAPI,
    onSuccess: (data) => {
      toast.success(tActionMessages.taskUpdatedSuccessfully);

      // Update cache
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task', data.taskId] });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.errors[0]?.message || 'Error updating task';
      toast.error(errorMessage);
    },
  });

  const fetchTasks = (newProjectId: string) => {
    queryClient.invalidateQueries({ queryKey: ['tasks', newProjectId] });
  };

  const setTasks = (newTasks: Task[]) => {
    // For backwards compatibility - this doesn't really make sense with React Query
    console.warn(
      'setTasks is deprecated when using React Query. Use refetch() instead.',
    );
  };

  const fetchTaskById = async (taskId: string): Promise<Task | null> => {
    if (!taskId) {
      throw new Error('Task ID is required');
    }

    try {
      return await fetchTaskByIdAPI(taskId);
    } catch (e: any) {
      const errorMessage =
        e?.response?.errors[0]?.message || 'Error fetching task';
      console.error('Error fetching task by ID:', errorMessage);
      toast.error(errorMessage);
      return null;
    }
  };

  const cancelDelegation = async (taskId: string): Promise<Task | null> => {
    if (!taskId) {
      throw new Error('Task ID is required');
    }

    try {
      return await cancelDelegationMutation.mutateAsync(taskId);
    } catch (error) {
      return null;
    }
  };

  const updateTask = async (taskUpdateInput: any): Promise<Task | null> => {
    if (!taskUpdateInput) {
      throw new Error('Task update input is required');
    }

    try {
      return await updateTaskMutation.mutateAsync(taskUpdateInput);
    } catch (error) {
      return null;
    }
  };

  return {
    tasks,
    loading:
      loading ||
      cancelDelegationMutation.isPending ||
      updateTaskMutation.isPending,
    error: queryError?.message || null,
    fetchTasks,
    fetchTaskById,
    setTasks,
    refetch,
    cancelDelegation,
    updateTask,
  };
};
